import { zodResolver } from "@hookform/resolvers/zod";
import { type UpdateProfilePayload, UploadContextType, updateProfileSchema } from "@upsquad/shared";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getUser } from "@/src/auth/store/auth-selectors";
import { setUser } from "@/src/auth/store/auth-slice";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { PhotoUpload } from "@/src/common/components/photo-upload";
import { Button, FieldError, Input, Label, Spinner, TextField } from "@/src/common/components/ui";
import { PRIVACY_URL, TOS_URL } from "@/src/common/constants/urls";
import { getSubdomain } from "@/src/common/lib/subdomain";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { OnboardingLayout } from "@/src/onboarding/components/onboarding-layout";
import { TestimonialsCarousel } from "@/src/onboarding/components/testimonials-carousel";
import { ONBOARDING_ROUTES } from "@/src/onboarding/constants/onboarding-routes";
import { useUpdateOnboardingProfileMutation } from "@/src/onboarding/services/onboarding-http.service";

export default function ProfilePage() {
	const { t } = useTranslation("onboarding");
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const user = useAppSelector(getUser);
	const [updateProfile, { isLoading }] = useUpdateOnboardingProfileMutation();
	const [photoUrl, setPhotoUrl] = useState<string | null>(user?.photo_url ?? null);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<UpdateProfilePayload>({
		resolver: zodResolver(updateProfileSchema),
		defaultValues: {
			first_name: user?.first_name ?? "",
			last_name: user?.last_name ?? "",
			photo_url: user?.photo_url ?? undefined,
		},
	});

	const onSubmit = async (data: UpdateProfilePayload) => {
		const payload = { ...data, photo_url: photoUrl ?? undefined };
		const result = await updateProfile(payload).unwrap();
		if (result) {
			dispatch(setUser(result));
			const subdomain = getSubdomain();
			if (subdomain) {
				navigate(ONBOARDING_ROUTES.JOIN_COMMUNITY);
			} else {
				navigate(ONBOARDING_ROUTES.CREATE_COMMUNITY_PROFILE);
			}
		}
	};

	return (
		<>
			<PageHelmet title="Profile Completion" />
			<OnboardingLayout leftContent={<TestimonialsCarousel />} step={1}>
				<h1 className="text-2xl font-bold mb-2">{t("profile_title")}</h1>
				<p className="text-sm text-foreground/50 mb-8">{t("profile_subtitle")}</p>

				<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
					<TextField isReadOnly isDisabled>
						<Label>{t("email_label")}</Label>
						<Input value={user?.email ?? ""} />
					</TextField>

					<div className="grid grid-cols-2 gap-3">
						<Controller
							name="first_name"
							control={control}
							render={({ field }) => (
								<TextField isInvalid={!!errors.first_name} isRequired>
									<Label>{t("first_name_label")}</Label>
									<Input
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										placeholder={t("first_name_placeholder")}
									/>
									{errors.first_name?.message && <FieldError>{errors.first_name.message}</FieldError>}
								</TextField>
							)}
						/>
						<Controller
							name="last_name"
							control={control}
							render={({ field }) => (
								<TextField isInvalid={!!errors.last_name} isRequired>
									<Label>{t("last_name_label")}</Label>
									<Input
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										placeholder={t("last_name_placeholder")}
									/>
									{errors.last_name?.message && <FieldError>{errors.last_name.message}</FieldError>}
								</TextField>
							)}
						/>
					</div>

					<PhotoUpload
						value={photoUrl}
						onChange={setPhotoUrl}
						context={{ type: UploadContextType.USER_AVATAR }}
						fallbackText={user?.first_name?.[0]?.toUpperCase() ?? "?"}
					/>

					<Button type="submit" variant="primary" fullWidth isPending={isLoading} className="mt-2">
						{({ isPending }) => (
							<>
								{isPending ? <Spinner color="current" size="sm" /> : null}
								{t("next")}
							</>
						)}
					</Button>

					<p className="text-xs text-foreground/40 text-center">
						{t("select_terms")}{" "}
						<a href={TOS_URL} className="text-accent underline">
							{t("select_tos")}
						</a>
						{". "}
						{t("select_read_our")}{" "}
						<a href={PRIVACY_URL} className="text-accent underline">
							{t("select_privacy")}
						</a>
					</p>
				</form>
			</OnboardingLayout>
		</>
	);
}
